import React, { useMemo, useState } from 'react'
import { Divider, Typography, useMediaQuery } from '@mui/material'
import Button from '../nura/button/button'
import Icon from '../icon'
import styles from './styles.module.css'
import useFetch from '../../hooks/useFetch'
import { deleteConnection, listConnections } from '../../api-client/data'
import ConnectApiKeyDialog from '../dialogs/connect-api-key'
import { useDispatch, Actions } from '../../stores/dashboard.store'
import CircularProgress from '@mui/material/CircularProgress'
import { postConnection, getCurrentPlan } from '../../api-client/data'
import { useSnackbar } from "../snackbar/snackbar"
import DeleteConnection from '../dialogs/delete-connection'
import dashboardStyles from '../dashboard/styles.module.css'

export default () => {
    const connections = useFetch(listConnections)
    const { enqueueSnackbar } = useSnackbar()
    const [activeModal, setActiveModal] = useState()
    const [loadingSpinner, setLoadingSpinner] = useState()
    const dispatch = useDispatch()

    const plan = useFetch(getCurrentPlan)

    const isMobile = useMediaQuery('(max-width: 900px)')
    
    const connectionsTypes = [
        {
            id: 'openai',
            name: 'OpenAI',
            description: 'Make use of OpenAI services utilizing the developer API',
            icon: (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                    <Icon type="openai" width={isMobile ? 30 : 60} height={isMobile ? 30 : 60} />
                </div>
            ),
            apiKeyLocation: (
                <p style={{ color: 'var(--text-secondary)' }}>
                    You can find your API keys by navigating to{' '}
                    <a href="https://platform.openai.com/settings/organization/general">the OpenAI platform</a> and finding <span>API Keys</span> under the project navigation
                </p>
            )
        },
        {
            id: 'elevenlabs',
            name: 'ElevenLabs',
            description: '11Labs voice cloning and utilizing the developer API',
            icon: (<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '5px' }}><Icon type="11labs" width={isMobile ? 60 : 90} height={isMobile ? 60 : 90} /></div>),
            apiKeyLocation: (
                <p style={{ color: 'var(--text-secondary)' }}>
                    You can find your APIs listed by navigating to{' '}
                    <a href="https://elevenlabs.io/app/settings/api-keys">the ElevenLabs platform</a>
                </p>
            )
        },
        {
            id: 'azure-ai-foundry',
            name: 'Azure AI Foundry',
            description: 'Azure AI foundry for Azure hosted AI models',
            icon: (<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '5px', width:isMobile ? 60 : 90, height: isMobile ? 60 : 90 }}><Icon type="azure" width={isMobile ? 60 : 50}  height={isMobile ? 60 : 50} /></div>),
            apiKeyLocation: (
                <p style={{ color: 'var(--text-secondary)' }}>
                    This requires that you have Azure AI foundry setup. {' '}
                    You can find you keys and endpoint listed at the bottom fo the AI foundry directory entry.
                </p>
            ),
            inputs: ["key", "url"]
        }
    ]

    const connectionEstablishedMap = useMemo(() => connections?.data?.reduce((a, c) => {
        a[c?.accountId] = true
        return a
    }, {}) || {}, [connections?.data])

    const handleConnect = async (connection) => {
        setActiveModal(undefined)
        dispatch(Actions.SetIndeterminate, true)
        setLoadingSpinner(connection?.info?.id)

        try {
            await postConnection(connection?.info?.id, connection?.apiKey)
            enqueueSnackbar("Connection Added", { variant: "success" })
        } catch {
            enqueueSnackbar("Connection was not added", { variant: "error" })
        }

        setLoadingSpinner(undefined)
        connections.refresh()
        dispatch(Actions.SetIndeterminate, false)
    }

    const handleDelete = async () => {
        setActiveModal(undefined)
        dispatch(Actions.SetIndeterminate, true)

        try {
            const { id } = connections?.data?.find(x => x?.accountId === activeModal?.info?.id)
            await deleteConnection(id)
            enqueueSnackbar("Connection Disconnected", { variant: "success" })
        } catch {
            enqueueSnackbar("Connection was not disconnected, try again later", { variant: "error" })
        }

        setLoadingSpinner(undefined)
        connections.refresh()
        dispatch(Actions.SetIndeterminate, false)
    }

    return (
        <>
            {
                ({
                    [true]: null,
                    [activeModal?.type === "connect"]: <ConnectApiKeyDialog handleConnect={handleConnect} setShow={() => setActiveModal(undefined)} show details={activeModal?.info} />,
                    [activeModal?.type === "disconnect"]: <DeleteConnection handleDelete={handleDelete} setShow={() => setActiveModal(undefined)} show />,
                }).true
            }
            <div className={dashboardStyles.dashboardContent}>
                <div className={dashboardStyles.dashboardHeader}>
                    <div className={dashboardStyles.settingsTitle} style={{ flex: 1 }}>
                        <Typography variant="h6">Connections</Typography>
                    </div>
                </div>
                {
                    ({
                        [true]: (
                            <div className={styles.connectionsScrolling}>
                                <div className={styles.connectionContainer}>
                                    {connectionsTypes.map(x => (
                                        <div className={styles.connectionItem} key={x.id}>
                                            <div className={styles.iconContainer}>
                                                {x?.icon}
                                            </div>
                                            <div className={styles.connectionText} style={{ flex: 1 }}>
                                                <div className={styles.connectionsName}>{x?.name}</div>
                                                <div>{x?.description}</div>
                                            </div>
                                            <div className={styles.connectionButtonContainer}>
                                                {
                                                    ({
                                                        [true]: <Button variant="primary" size="normal" onClick={() => setActiveModal({ type: 'connect', info: x })}>Connect</Button>,
                                                        [loadingSpinner === x?.id]: <CircularProgress size="30px" />,
                                                        [connectionEstablishedMap[x?.id] !== undefined]: <Button variant="outline" size="normal" onClick={() => setActiveModal({ type: 'disconnect', info: x })}>Disconnect</Button>
                                                    }).true
                                                }
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ),
                        [plan?.data?.plan?.name === 'free']: (
                            <div style={{ padding: '15px 30px', color: 'var(--text-primary)' }}>
                                Connection is not available in free tier. Upgrade to add connections
                            </div>
                        ),
                        [connections.loading === true || plan.loading === true]: (
                            <div style={{ padding: '25px' }}>
                                <CircularProgress />
                            </div>
                        )
                    }).true
                }
            </div>
        </>
    )
}